<script lang="ts">
    import { parseDate } from '@internationalized/date';
    import dayjs from 'dayjs';
    import { onDestroy, onMount } from 'svelte';

    import { currency as currentCurrency } from '$saga/store/currency';
    import type { OptionList } from '$saga/types/search.types';
    import { Currency } from '$saga/utils/currency';
    import {
        filteredToursCount,
        filtersContext,
        setBudgetFilter,
        setDurationFilter,
        sortValue,
    } from '$saga/views/Search/store';
    import {
        Sorting,
        type LandingPageContext,
        type SearchForm,
    } from '$saga/views/Search/types';
    import {
        getSortValue,
        handleFormSubmissionAnalytics,
        initializeLabelValues,
        SEARCH_PAGE_FORM_ID,
    } from '$saga/views/Search/utils';

    export let defaultDestinationOptions: OptionList;
    export let defaultSportOptions: OptionList;
    export let defaultDurationMin: number;
    export let defaultDurationMax: number;
    export let defaultBudgetMin: number;
    export let defaultBudgetMax: number;
    export let defaultLevels: OptionList;
    export let defaultTags: OptionList;
    export let defaultSkiResorts: OptionList;
    export let defaultTypes: OptionList;
    export let alpineSkiingSportId: string | null;
    export let searchForm: SearchForm | undefined = undefined;
    export let currency: Currency;
    export let initialFilteredToursCount: number | null = null;
    export let landingPageContext: LandingPageContext = {
        isLandingPage: false,
        type: null,
        pillarFilters: {},
    };

    $filtersContext.defaultDurationMin = defaultDurationMin;
    $filtersContext.defaultDurationMax = defaultDurationMax;
    $filtersContext.defaultBudgetMin = defaultBudgetMin;
    $filtersContext.defaultBudgetMax = defaultBudgetMax;

    $filtersContext.formValues = {
        destination: searchForm?.destinations
            ? searchForm.destinations.filter((v) => v != null).map(String)
            : [],
        theme: searchForm?.sports
            ? searchForm.sports.filter((v) => v != null).map(String)
            : [],
        startDate: searchForm?.startDate?.date
            ? parseDate(dayjs(searchForm.startDate.date).format('YYYY-MM-DD'))
            : undefined,
        endDate: searchForm?.endDate?.date
            ? parseDate(dayjs(searchForm.endDate.date).format('YYYY-MM-DD'))
            : undefined,
        months: searchForm?.months ?? [],
        flexibility: searchForm?.flexibility ?? 0,
        nbPax: searchForm?.nbPax?.toString() ?? '0',
        // adults: searchForm?.nbPax?.toString() ?? '0',
        // children: '0',
        // childrenAges: [],
        // secondary
        levels: searchForm?.levels ?? [],
        types: searchForm?.types ?? [],
        durationMin: null,
        durationMax: null,
        budgetMin: null,
        budgetMax: null,
        tags: searchForm?.tags ?? [],
        resorts: searchForm?.resorts?.map(String) ?? [],
        bestReviewed: searchForm?.bestReviewed ?? false,
        exclusiveSport: searchForm?.exclusiveSport ?? false,
        keywords: searchForm?.keywords ?? null,
    };

    setDurationFilter(
        searchForm?.durationMin ?? null,
        searchForm?.durationMax ?? null,
    );
    setBudgetFilter(
        searchForm?.budgetMin ?? null,
        searchForm?.budgetMax ?? null,
    );

    $currentCurrency = currency ?? Currency.EUR;

    $filtersContext.formLabels = initializeLabelValues(
        $filtersContext.formValues,
        defaultDestinationOptions,
        defaultSportOptions,
        defaultLevels,
        defaultTypes,
        defaultTags,
        defaultDurationMin,
        defaultDurationMax,
        defaultBudgetMin,
        defaultBudgetMax,
    );

    $filtersContext.defaultLevels = defaultLevels;
    $filtersContext.defaultTags = defaultTags;
    $filtersContext.defaultSkiResorts = defaultSkiResorts;
    $filtersContext.defaultTypes = defaultTypes;
    $filtersContext.defaultSportOptions = defaultSportOptions;
    $filtersContext.defaultDestinationOptions = defaultDestinationOptions;
    $filtersContext.alpineSkiingSportId = alpineSkiingSportId;
    $filtersContext.isSubmitting = false;
    // Set landing page context
    $filtersContext.isLandingPage = landingPageContext.isLandingPage;
    $filtersContext.landingPageType = landingPageContext.type;
    $filtersContext.pillarFilters = landingPageContext.pillarFilters;
    $sortValue =
        !searchForm?.sort || searchForm.sort === Sorting.DEFAULT
            ? getSortValue(landingPageContext)
            : searchForm.sort;

    if (initialFilteredToursCount !== null) {
        $filteredToursCount = initialFilteredToursCount;
    }

    const submitButtonEvent = () => {
        $filtersContext.isSubmitting = true;

        // hide the current result cards and reveal the skeleton container instead of destroying the DOM
        const resultCards = document.getElementById('result-cards');
        const skeleton = document.getElementById(
            'result-cards-skeleton-container',
        );
        if (resultCards) {
            resultCards.classList.add('hidden');
        }

        if (skeleton) {
            skeleton.classList.remove('hidden');
        }

        handleFormSubmissionAnalytics();
    };

    const resetButtonEvent = () => {
        $filtersContext.isSubmitting = false;
    };

    onMount(() => {
        // listen for the form submit event and add a load spinner on the submitButtonElement
        const form = document.getElementById(SEARCH_PAGE_FORM_ID);
        if (form) {
            form.addEventListener('submit', submitButtonEvent);
            form.addEventListener('reset', resetButtonEvent);
        }
    });

    onDestroy(() => {
        // remove event listener on form submit
        const form = document.getElementById(SEARCH_PAGE_FORM_ID);
        if (form) {
            form.removeEventListener('submit', submitButtonEvent);
            form.removeEventListener('reset', resetButtonEvent);
        }
    });
</script>
